/**
 * Audience — Demographics, geography, retention, and user segments
 * Data: GA4 Property 484092824 (Feb 26 – Mar 25, 2026)
 */

import React, { useMemo } from 'react';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { motion } from 'framer-motion';
import { staggerContainer, fadeInUp, pageTransition, pageInitial, pageAnimate } from '@/lib/motionVariants';
import { formatNumber } from '@/lib/formatting';
import { TEXT, BORDER, BRAND, CHART_COLORS, getChartTooltip, cssVar } from '../charts/utils';
import { useChatStore } from '@/stores/chatStore';
import { useThemeKey } from '@/hooks/useThemeKey';
import DeltaIndicator from '../shared/DeltaIndicator';
import { AUDIENCE_KPIS, RETENTION_COMPARISON } from '@/data/comparisonData';
import PageHeader from '../shared/PageHeader';
import { useComparisonPeriods } from '@/hooks/useComparisonPeriods';

const askChat = (query: string, e?: React.MouseEvent) => {
  if (e) {
    useChatStore.getState().showAnalyzePrompt(
      query, '', (e.currentTarget as HTMLElement).getBoundingClientRect(),
    );
  } else {
    useChatStore.getState().sendAnalyzeQuery(query);
  }
};

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Filler, Tooltip, Legend);

const COUNTRIES = [
  { country: 'United States', users: 70_000, pct: 98.59 },
  { country: 'Mexico',        users: 218,    pct: 0.31 },
  { country: 'Canada',        users: 118,    pct: 0.17 },
  { country: 'United Kingdom', users: 61,    pct: 0.09 },
  { country: 'India',         users: 55,     pct: 0.08 },
  { country: 'Germany',       users: 49,     pct: 0.07 },
];

const RETENTION = [
  { week: 'Week 0', rate: 100 },
  { week: 'Week 1', rate: 3.4 },
  { week: 'Week 2', rate: 1.7 },
  { week: 'Week 3', rate: 1.1 },
  { week: 'Week 4', rate: 0.8 },
  { week: 'Week 5', rate: 0.6 },
];

const USER_ACTIVITY = [
  { label: '30-day active users', value: 75_000 },
  { label: '7-day active users',  value: 17_000 },
  { label: '1-day active users',  value: 2_300 },
  { label: 'Realtime users',      value: 41 },
];

// TODO: Add useAudience() hook when /api/monarch/analytics/audience endpoint is built
// For now, audience/geo data continues using hardcoded values
const Audience: React.FC = () => {
  const themeKey = useThemeKey();
  const chartTooltip = getChartTooltip();
  const { compareEnabled, popTooltip, yoyTooltip } = useComparisonPeriods();
  const retentionData = useMemo(() => ({
    labels: RETENTION.map((r) => r.week),
    datasets: [{
      label: 'Returning Users %',
      data: RETENTION.map((r) => r.rate),
      borderColor: cssVar('--color-danger', '#ef4444'),
      backgroundColor: 'rgba(239, 68, 68, 0.08)',
      borderWidth: 2,
      pointRadius: 5,
      pointBackgroundColor: cssVar('--color-danger', '#ef4444'),
      tension: 0.3,
      fill: true,
    }],
  }), []);

  const geoDonut = useMemo(() => ({
    labels: COUNTRIES.map((c) => c.country),
    datasets: [{
      data: COUNTRIES.map((c) => c.users),
      backgroundColor: [BRAND.gold, CHART_COLORS[2], CHART_COLORS[1], CHART_COLORS[4], CHART_COLORS[3], CHART_COLORS[6]],
      borderWidth: 0,
    }],
  }), []);

  return (
    <motion.div className="space-y-6" initial={pageInitial} animate={pageAnimate} transition={pageTransition}>
      <PageHeader title="Audience" subtitle="Demographics, geography, retention, and user segments" />

      {/* KPIs */}
      <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-3" variants={staggerContainer} initial="hidden" animate="visible">
        {USER_ACTIVITY.map((item) => {
          const compKey = { '30-day active users': 'active30d', '7-day active users': 'active7d', '1-day active users': 'active1d', 'Realtime users': 'realtimeNow' }[item.label] as keyof typeof AUDIENCE_KPIS | undefined;
          const comp = compKey ? AUDIENCE_KPIS[compKey] : undefined;
          return (
            <motion.div key={item.label} variants={fadeInUp} className="bg-bg-1 border border-border rounded-xl p-4">
              <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">{item.label}</p>
              <p className="font-mono text-lg font-bold text-text">{formatNumber(item.value)}</p>
              {compareEnabled && comp && (
                <div className="mt-1">
                  <DeltaIndicator current={comp.current} yoyPrior={comp.yoy} popPrior={comp.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} />
                </div>
              )}
            </motion.div>
          );
        })}
      </motion.div>

      {/* Geography + Retention */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Geography */}
        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <h3 className="font-display text-sm text-text-2 mb-4">Users by Country</h3>
          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-center justify-center" style={{ height: 200 }}>
              <Doughnut
                key={`geo-donut-${themeKey}`}
                data={geoDonut}
                options={{
                  responsive: true, maintainAspectRatio: false, cutout: '60%',
                  plugins: {
                    legend: { display: false },
                    tooltip: chartTooltip,
                  },
                }}
              />
            </div>
            <div className="space-y-3 flex flex-col justify-center">
              {COUNTRIES.map((c, idx) => (
                <div key={c.country} className="flex items-center justify-between rounded-lg px-1 -mx-1 py-0.5">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: [BRAND.gold, CHART_COLORS[2], CHART_COLORS[1], CHART_COLORS[4], CHART_COLORS[3], CHART_COLORS[6]][idx] }}
                    />
                    <span className="text-xs text-text-2">{c.country}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-text">{formatNumber(c.users)}</span>
                    <span className="text-xs font-mono text-text-3 w-12 text-right">{c.pct}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 p-3 rounded-lg bg-monarch-gold/5 border border-monarch-gold/20">
            <p className="text-xs text-monarch-gold font-medium">98.6% US-based audience</p>
            <p className="text-xs text-text-3 mt-1">
              Hyper-local demand. Mexico (0.31%) is the top international market — potential for cross-border targeting in Laredo/Monterrey corridor.
            </p>
          </div>
        </div>

        {/* Retention */}
        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <h3 className="font-display text-sm text-text-2 mb-4">User Retention Cohort</h3>
          <div style={{ height: 220 }}>
            <Line
              key={`retention-${themeKey}`}
              data={retentionData}
              options={{
                responsive: true, maintainAspectRatio: false,
                scales: {
                  x: { grid: { display: false }, ticks: { color: TEXT.faint, font: { size: 10 } }, border: { display: false } },
                  y: { min: 0, max: 100, grid: { color: BORDER.default }, ticks: { color: TEXT.faint, font: { size: 10 }, callback: (v) => `${v}%` }, border: { display: false } },
                },
                plugins: {
                  legend: { display: false },
                  tooltip: { ...chartTooltip, callbacks: { label: (ctx) => `${ctx.parsed.y}% returning` } },
                },
              }}
            />
          </div>
          <div className="mt-4 p-3 rounded-lg bg-danger/5 border border-danger/20">
            <p className="text-xs text-danger font-medium">96.6% drop-off after first visit</p>
            <p className="text-xs text-text-3 mt-1">
              Only 3.4% return after week 1. By week 5, just 0.6% remain. This is typical for hotel sites with single-purpose visits, but email capture and retargeting could double return rates.
            </p>
          </div>
        </div>
      </div>

      {/* Retention Table */}
      <div className="bg-bg-1 border border-border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="font-display text-sm text-text-2">Retention Cohort Detail</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-bg-2 text-text-3">
                <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">Cohort Week</th>
                <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Return Rate</th>
                {compareEnabled && <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Change</th>}
                <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Drop from Prior</th>
                <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">Retention</th>
              </tr>
            </thead>
            <tbody>
              {RETENTION.map((row, idx) => {
                const dropFromPrior = idx > 0
                  ? RETENTION[idx - 1].rate - row.rate
                  : 0;
                return (
                  <tr key={idx} className="border-b border-border/50 hover:bg-bg-2 transition-colors">
                    <td className="px-4 py-3 font-medium text-text">{row.week}</td>
                    <td className="px-4 py-3 text-right font-mono text-text">{row.rate}%</td>
                    {compareEnabled && (
                      <td className="px-4 py-3 text-right">
                        {RETENTION_COMPARISON[row.week] && (
                          <DeltaIndicator current={row.rate} yoyPrior={RETENTION_COMPARISON[row.week].yoy} popPrior={RETENTION_COMPARISON[row.week].pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right font-mono text-danger">
                      {idx > 0 ? `-${dropFromPrior.toFixed(1)}pp` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="w-full h-3 bg-bg-2 rounded-full overflow-hidden" style={{ maxWidth: 200 }}>
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${row.rate}%`,
                            backgroundColor: row.rate > 50 ? cssVar('--color-success', '#10b981') : row.rate > 5 ? cssVar('--color-warning', '#f59e0b') : cssVar('--color-danger', '#ef4444'),
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-monarch-gold/5 border border-monarch-gold/30 rounded-xl p-4 cursor-pointer hover:brightness-110 transition-all" onClick={(e) => askChat('99%+ of visitors are new with zero re-engagement. How can we build email capture and retargeting from the 13K reservation-interested users?', e)}>
          <p className="text-sm font-semibold text-text mb-1">New vs Returning: 99%+ New</p>
          <p className="text-xs text-text-3 leading-relaxed">
            71K active users and 71K new users — nearly every visitor is a first-timer. This makes sense for a hotel discovery site but signals zero re-engagement infrastructure. Building an email list from the 13K reservation-interested users could create a retargetable audience.
          </p>
        </div>
        <div className="bg-info/5 border border-info/30 rounded-xl p-4 cursor-pointer hover:brightness-110 transition-all" onClick={(e) => askChat('GA4 flagged anomalies on March 2, 2026 — spikes in Direct users, sessions, and scroll events. What could have caused this and how do we replicate it?', e)}>
          <p className="text-sm font-semibold text-text mb-1">March 2 Spike Detected</p>
          <p className="text-xs text-text-3 leading-relaxed">
            GA4 flagged multiple anomalies on March 2, 2026: spikes in Direct users, total sessions, returning users, and scroll events. This could be a marketing push, PR coverage, or event-driven traffic. Investigate what drove this to replicate it.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default Audience;
